import { useEffect, useRef, useState } from 'react';
import Stats from 'stats.js';
import { useNavStore } from '../store/useNavStore';
import { DebugHUDPerfReadout } from '../perf/DebugHUDPerfPanel';

/**
 * Dev-only overlay gated on `?debug=1` in the URL.
 *
 * Left side: stats.js FPS panel (MS / FPS via stats.showPanel(0)).
 * Right side: navState + camera position + boundaryHit flag — the three
 *             quantities a dev cares about mid-walk.
 *
 * Hidden behind a query param so production visitors never see it. stats.js
 * DOM is appended/removed inside a useEffect that short-circuits when the
 * param is absent.
 */

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const qs = new URLSearchParams(window.location.search);
  return qs.get('debug') === '1';
}

export function DebugHUD() {
  const navState = useNavStore((s) => s.navState);
  const cameraPosition = useNavStore((s) => s.cameraPosition);
  const boundaryHit = useNavStore((s) => s.boundaryHit);
  const statsRef = useRef<Stats | null>(null);
  const [enabled] = useState(isDebugEnabled());

  useEffect(() => {
    if (!enabled) return;
    const stats = new Stats();
    stats.showPanel(0); // FPS
    stats.dom.style.position = 'fixed';
    stats.dom.style.top = '0.5rem';
    stats.dom.style.left = '0.5rem';
    stats.dom.style.zIndex = '200';
    document.body.appendChild(stats.dom);
    statsRef.current = stats;

    let raf = 0;
    const loop = () => {
      stats.update();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      if (stats.dom.parentNode) {
        stats.dom.parentNode.removeChild(stats.dom);
      }
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: '0.5rem',
        right: '0.5rem',
        padding: '0.5rem 0.75rem',
        background: 'rgba(59,47,47,0.85)',
        color: '#FDF6EE',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: '0.75rem',
        lineHeight: 1.4,
        borderRadius: 4,
        zIndex: 200,
        pointerEvents: 'none',
      }}
    >
      <div>
        navState: <strong>{navState}</strong>
      </div>
      <div>
        cam: [{cameraPosition.map((n) => n.toFixed(2)).join(', ')}]
      </div>
      <div>boundaryHit: {boundaryHit ? 'TRUE' : 'false'}</div>
      {/* Plan 07-05 — gl.info readout (calls / tri / points / lines / geos / tex).
          Samples via DebugHUDPerfProbe mounted in SceneGraph; this is the DOM
          readout. Gated internally on ?debug=1. */}
      <DebugHUDPerfReadout />
    </div>
  );
}
